import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { MobileDialog } from "../components/mobile";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar, Clock, CreditCard, AlertTriangle, X,
  Check, ChevronRight, Loader2, Shield, Ban, Bell,
} from "lucide-react";
import { Button } from "../components/ui/button";
import useAsyncAction from "../hooks/useAsyncAction";
import CalmEmptyState from "../components/learner/CalmEmptyState";
import { isActiveLesson, isRemovedFromActiveDiary } from "../lib/lessonLifecycle";
import PupilFinancialHistory from "../components/payments/PupilFinancialHistory";
import LessonPreparation from "../components/learner/LessonPreparation";
import BookingCalendar from "../components/learner/BookingCalendar";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

const durLabel = (mins) =>
  mins === 60 ? "1 Hour" : mins === 90 ? "90 Min" : mins === 120 ? "2 Hours" : `${mins}min`;

export default function MyBookings() {
  const { user } = useAuth();
  const learnerKey = user?.founder_mode_subject_user_id || user?.id || user?._id;
  if (!learnerKey) return null;
  const scope = `${learnerKey}:${user?.operating_mode || ""}:${user?.admin_view_role || ""}`;
  return <BookingsContent key={scope} learnerKey={learnerKey} />;
}

function BookingsContent({ learnerKey }) {
  const [bookings, setBookings] = useState([]);
  const [policy, setPolicy] = useState({ cancellation_window_hours: 24, refund_mode: "learner_choice" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const requestRef = useRef(0);
  const [cancelModal, setCancelModal] = useState(null);
  const [refundChoice, setRefundChoice] = useState("credit"); // credit | refund
  // Shared async-action contract — blocks double-tap on Cancel and refetches.
  const { run, busy: cancelling } = useAsyncAction();

  const fetchBookings = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/lesson-payments/my-bookings/all`, { withCredentials: true, timeout: 20000 });
      if (request !== requestRef.current) return;
      if (!Array.isArray(data?.bookings)) throw new Error("Invalid booking response");
      setBookings(data.bookings);
      setPolicy({
        cancellation_window_hours: data.cancellation_window_hours || 24,
        refund_mode: data.refund_mode || "learner_choice",
      });
    } catch {
      if (request === requestRef.current) setError("Your lessons could not be loaded. Please try again.");
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
    return () => { requestRef.current++; };
  }, [fetchBookings]);

  const handleCancel = (lessonId) =>
    run(
      () =>
        axios.post(
          `${API}/api/lesson-payments/cancel/${lessonId}`,
          { refund_choice: refundChoice },
          { withCredentials: true }
        ),
      {
        errorMessage: "Cancellation failed — please try again.",
        onSuccess: async (data) => {
          // Backend returns the canonical message — surface it as-is.
          toast.success(data?.message || "Lesson cancelled.");
          setCancelModal(null);
          setRefundChoice("credit");
          await fetchBookings();
        },
      }
    );

  if (loading) {
    return (
      <div data-testid="my-bookings-loading" className="py-6 text-center">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" data-testid="my-bookings-error" className="rounded-xl border border-amber-300 p-4 space-y-3">
        <p>{error}</p>
        <button type="button" onClick={fetchBookings} className="min-h-11 px-4 rounded-lg bg-blue-700 text-white font-semibold">Retry lessons</button>
      </div>
    );
  }

  const visible = bookings.filter(b => !selectedDate || b.date === selectedDate)
    .sort((a, b) => `${a.date} ${a.start_time}`.localeCompare(`${b.date} ${b.start_time}`));
  const upcoming = visible.filter(b => !b.is_past && isActiveLesson(b));
  const cancelled = visible.filter(b => isRemovedFromActiveDiary(b));
  const past = visible.filter(b => b.is_past && isActiveLesson(b)).reverse();

  return (
    <div data-testid="my-bookings" className="space-y-4">
      <BookingCalendar bookings={bookings} selectedDate={selectedDate} onSelectDate={setSelectedDate} />
      {selectedDate && <div className="flex items-center justify-between gap-2 text-sm" role="status">
        <p>Lessons on {selectedDate.split("-").reverse().join("-")}</p>
        <button type="button" className="min-h-11 px-2 text-blue-700 dark:text-blue-300" onClick={() => setSelectedDate(null)}>Show all lessons</button>
      </div>}
      {visible.length === 0 && <CalmEmptyState testid="my-bookings-empty" tone="nothing-yet"
        title={selectedDate ? "No lessons on this date" : "No bookings yet"}
        body={selectedDate ? "Choose another date or book a lesson." : "Book your first lesson to get started — your lessons will appear here."} />}

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Upcoming</h3>
          {upcoming.map((b) => (
            <BookingCard key={b.id} booking={b} learnerKey={learnerKey} onCancel={() => setCancelModal(b)} />
          ))}
        </div>
      )}

      {/* Past */}
      {past.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Past</h3>
          {past.slice(0, 5).map((b) => (
            <BookingCard key={b.id} booking={b} isPast />
          ))}
        </div>
      )}

      {/* Cancelled */}
      {cancelled.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Cancelled</h3>
          {cancelled.slice(0, 3).map((b) => (
            <BookingCard key={b.id} booking={b} isCancelled />
          ))}
        </div>
      )}

      <details className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
        <summary className="min-h-11 flex items-center cursor-pointer font-semibold text-sm">Payments and lesson credit</summary>
        <PupilFinancialHistory endpoint="/api/lesson-payments/financial-history" />
      </details>

      {/* Cancel Confirmation Modal */}
      {cancelModal && (
        <MobileDialog
          bareContent
          open={!!cancelModal}
          onClose={() => setCancelModal(null)}
          testid="cancel-modal-wrap"
          presentation="center"
        >
          <div data-testid="cancel-modal" className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl mx-3">
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Cancel This Lesson?</h3>
                  <p className="text-xs text-slate-400">
                    {new Date(cancelModal.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                    {" at "}{fmtTime(cancelModal.start_time)}
                  </p>
                </div>
              </div>

              {/* Policy explanation */}
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                {cancelModal.refund_eligible ? (
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-emerald-700 dark:text-emerald-400">Eligible for lesson credit</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        This lesson is more than {policy.cancellation_window_hours} hours away — choose how
                        you'd like £{Number(cancelModal.amount || 0).toFixed(2)} returned to you.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <Ban className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-red-700 dark:text-red-400">Cancellation locked</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        This lesson is within {policy.cancellation_window_hours} hours. Please contact your
                        instructor — they can override if appropriate.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Refund choice (only when eligible) */}
              {cancelModal.refund_eligible && (
                <div data-testid="refund-choice-group" className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">What happens next</p>
                  <label
                    data-testid="refund-choice-credit"
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      refundChoice === "credit"
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/15"
                        : "border-slate-200 dark:border-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="refund_choice"
                      value="credit"
                      checked={refundChoice === "credit"}
                      onChange={() => setRefundChoice("credit")}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-extrabold text-slate-900 dark:text-white">Lesson credit</p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        £{Number(cancelModal.amount || 0).toFixed(2)} stored on your account — applied automatically to your next booking.
                      </p>
                    </div>
                  </label>

                </div>
              )}

              <div className="flex gap-2">
                <Button data-testid="cancel-modal-back" onClick={() => { setCancelModal(null); setRefundChoice("credit"); }}
                  variant="outline" className="flex-1 h-11 rounded-xl">
                  Keep Lesson
                </Button>
                <Button data-testid="cancel-modal-confirm" onClick={() => handleCancel(cancelModal.id)}
                  disabled={cancelling || !cancelModal.refund_eligible}
                  className="flex-1 h-11 rounded-xl bg-red-600 hover:bg-red-700 text-white disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500">
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : (cancelModal.refund_eligible ? "Cancel Lesson" : "Locked")}
                </Button>
              </div>
            </div>
          </div>
        </MobileDialog>
      )}
    </div>
  );
}

function BookingCard({ booking, learnerKey, onCancel, isPast, isCancelled }) {
  const location = useLocation();
  const b = booking;
  const dateObj = new Date(b.date + "T00:00:00");
  const dateStr = dateObj.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

  let statusBadge = null;
  if (isCancelled) {
    const refundStatus = b.refund_status;
    statusBadge = (
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
        refundStatus === "credited" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
        : refundStatus === "forfeited" ? "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
        : refundStatus === "refunded" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
        : "bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
      }`}>
        {refundStatus === "credited" ? "Credited"
          : refundStatus === "refunded" ? "Refunded"
          : refundStatus === "forfeited" ? "Forfeited"
          : "Refund Pending"}
      </span>
    );
  } else if (b.paid_at) {
    // Surface-2 Truth Hardening (Feb 2026): "Paid" wording is gated on
    // the authoritative `paid_at` timestamp written by the Stripe
    // webhook or an operator-authorised manual mark-paid. The
    // `payment_status` enum alone is NOT sufficient authority.
    statusBadge = (
      <span data-testid={`paid-badge-${b.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">Paid</span>
    );
  } else if (b.payment_status === "payment_due") {
    statusBadge = (
      <span data-testid={`payment-due-badge-${b.id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">Payment due</span>
    );
  }

  // 3-day in-app reminder badge — calm amber pill on confirmed lessons within 72h.
  const reminderBadge = b.reminder_active ? (
    <span
      data-testid={`reminder-badge-${b.id}`}
      className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400"
    >
      <Bell className="w-2.5 h-2.5" />
      {b.hours_until_lesson < 24
        ? "Today"
        : `In ${Math.floor((b.hours_until_lesson || 0) / 24)} day${Math.floor((b.hours_until_lesson || 0) / 24) === 1 ? "" : "s"}`}
    </span>
  ) : null;

  return (
    <div data-testid={`booking-${b.id}`}
      id={`row-booking-${b.id}`}
      className={`rounded-2xl border p-4 transition-all ${
        isCancelled
          ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 opacity-70"
          : isPast
          ? "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
      }`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCancelled ? "bg-slate-200 dark:bg-slate-700" : isPast ? "bg-slate-100 dark:bg-slate-700" : "bg-blue-100 dark:bg-blue-900/30"
        }`}>
          {isCancelled ? <X className="w-5 h-5 text-slate-400" /> : <Calendar className={`w-5 h-5 ${isPast ? "text-slate-400" : "text-blue-600"}`} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-bold ${isCancelled ? "text-slate-400 line-through" : "text-slate-900 dark:text-white"}`}>{dateStr}</p>
            {statusBadge}
            {reminderBadge}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtTime(b.start_time)} — {durLabel(b.duration_minutes)}</span>
            {b.amount > 0 && <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" />£{Number(b.amount).toFixed(2)}</span>}
          </div>
          {isCancelled && b.cancelled_at && (
            <p className="text-[10px] text-slate-400 mt-1">
              Cancelled {new Date(b.cancelled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
            </p>
          )}
        </div>
        {onCancel && b.can_cancel && (
          <button data-testid={`cancel-btn-${b.id}`} onClick={onCancel}
            className="text-xs font-bold text-red-500 hover:text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex-shrink-0">
            Cancel
          </button>
        )}
      </div>
      {!isPast && !isCancelled && learnerKey && (
        <LessonPreparation key={`${learnerKey}:${b.id}`} lessonId={b.id}
          initialOpen={new URLSearchParams(location.search).get("prepare_lesson") === b.id} />
      )}
    </div>
  );
}

